import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../config/supabase';
import { useAdminCheck } from '../../hooks/useAdminCheck';
import LoadingSpinner from '../../components/LoadingSpinner';
import PageHeader from '../../components/PageHeader';

const isLocal =
  window.location.hostname === "localhost" ||
  window.location.hostname === "127.0.0.1" ||
  window.location.hostname.endsWith(".app.github.dev");

const getApiUrl = () => {
  const baseUrl = import.meta.env.VITE_FUNCTION_URL;
  return `${baseUrl}/api/AskOpenAIBatchList`;
};

const OpenAIBatchesPage = () => {
  const { isAdmin, loading: adminLoading } = useAdminCheck();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshLoading, setRefreshLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [functionKey, setFunctionKey] = useState(null);
  const [keyLoading, setKeyLoading] = useState(true);
  const [functionKeyMissing, setFunctionKeyMissing] = useState(false);
  const [downloadingFiles, setDownloadingFiles] = useState({});
  const [cancellingBatches, setCancellingBatches] = useState({});
  const [viewingFile, setViewingFile] = useState(null);
  const [fileContent, setFileContent] = useState([]);
  const [loadingFileContent, setLoadingFileContent] = useState(false);

  // Récupération de la clé de fonction
  useEffect(() => {
    const getFunctionKey = async () => {
      try {
        const keyName = isLocal ? 'FUNCTION_KEY_DEV' : 'FUNCTION_KEY';
        const { data, error } = await supabase
          .from('keys')
          .select('value')
          .eq('env', keyName)
          .single();
        if (error) {
          setFunctionKeyMissing(true);
        } else if (data) {
          setFunctionKey(data.value);
          setFunctionKeyMissing(false);
        } else {
          setFunctionKeyMissing(true);
        }
      } catch (err) {
        setFunctionKeyMissing(true);
      } finally {
        setKeyLoading(false);
      }
    };
    getFunctionKey();
  }, []);

  // Fonction native pour formater les dates
  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    try {
      let date;
      // Si c'est un nombre (timestamp Unix), multiplier par 1000
      if (typeof timestamp === 'number') {
        date = new Date(timestamp * 1000);
      } else {
        // Sinon, traiter comme une chaîne ISO
        date = new Date(timestamp);
      }
      
      // Vérifier si la date est valide
      if (isNaN(date.getTime())) {
        return '-';
      }
      
      const lang = i18n.language || 'fr';
      return date.toLocaleString(lang, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return '-';
    }
  };

  // Fonction pour charger les batchs depuis la base
  const loadBatches = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('openai_batches')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setBatches(data || []);
    } catch (error) {
      console.error("Erreur lors du chargement des batchs:", error);
      showMessage(t('settings.admin.openAIBatches.errorFetchingBatches'), 'error');
    } finally {
      setLoading(false);
    }
  };

  // Charger les batchs au montage
  useEffect(() => {
    if (!adminLoading && isAdmin) {
      loadBatches();
    }
  }, [adminLoading, isAdmin]);

  // Fonction pour rafraîchir un batch individuel via l'API
  const refreshSingleBatch = async (batchId) => {
    try {
      if (!functionKey) {
        throw new Error(t('settings.admin.openAIBatches.functionKeyMissing'));
      }

      const apiUrl = getApiUrl();
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          "x-functions-key": functionKey
        },
        body: JSON.stringify({ batchId })
      });

      const result = await response.json();
      
      if (response.ok && result.success) {
        return true;
      } else {
        console.error(`Erreur lors du rafraîchissement du batch ${batchId}:`, result.error);
        return false;
      }
    } catch (error) {
      console.error(`Erreur lors du rafraîchissement du batch ${batchId}:`, error);
      return false;
    }
  };

  // Fonction pour rafraîchir manuellement tous les batchs via l'API
  const refreshBatches = async () => {
    setRefreshLoading(true);
    try {
      if (!functionKey) {
        throw new Error(t('settings.admin.openAIBatches.functionKeyMissing'));
      }

      const apiUrl = getApiUrl();
      const response = await fetch(apiUrl, {
        method: "GET",
        headers: { 
          "x-functions-key": functionKey
        }
      });

      const result = await response.json();
      
      if (response.ok && result.success) {
        showMessage(t('settings.admin.openAIBatches.refreshSuccess'), 'success');
        await loadBatches();
      } else {
        throw new Error(result.error || 'API error');
      }
    } catch (error) {
      console.error("Erreur lors du rafraîchissement:", error);
      showMessage(`${t('settings.admin.openAIBatches.errorRefreshing')}: ${error.message}`, 'error');
    } finally {
      setRefreshLoading(false);
    }
  };

  // Afficher un message temporaire
  const showMessage = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 3000);
  };

  // Fonction pour obtenir la couleur du statut
  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      case 'in_progress':
        return 'bg-yellow-100 text-yellow-800';
      case 'validating':
      case 'finalizing':
        return 'bg-blue-100 text-blue-800';
      case 'expired':
      case 'cancelled':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Fonction pour télécharger un fichier depuis OpenAI
  const downloadFile = async (fileId, fileName, batchId) => {
    const downloadKey = `${batchId}-${fileId}`;
    try {
      if (!functionKey) {
        throw new Error(t('settings.admin.openAIBatches.functionKeyMissing'));
      }

      setDownloadingFiles(prev => ({ ...prev, [downloadKey]: true }));

      const baseUrl = import.meta.env.VITE_FUNCTION_URL;
      const apiUrl = `${baseUrl}/api/AskOpenAIFileDownload`;

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          "x-functions-key": functionKey
        },
        body: JSON.stringify({ fileId })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      showMessage(t('settings.admin.openAIBatches.downloadSuccess'), 'success');
    } catch (error) {
      console.error("Erreur lors du téléchargement:", error);
      showMessage(`${t('settings.admin.openAIBatches.downloadErrorMessage')}: ${error.message}`, 'error');
    } finally {
      setDownloadingFiles(prev => {
        const newState = { ...prev };
        delete newState[downloadKey];
        return newState;
      });
    }
  };

  // Fonction pour visualiser le contenu d'un fichier
  const viewFileContent = async (fileId, fileType, batchId) => {
    try {
      if (!functionKey) {
        throw new Error(t('settings.admin.openAIBatches.functionKeyMissing'));
      }

      setLoadingFileContent(true);
      setViewingFile({ fileId, fileType, batchId });

      const baseUrl = import.meta.env.VITE_FUNCTION_URL;
      const apiUrl = `${baseUrl}/api/AskOpenAIFileDownload`;

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          "x-functions-key": functionKey
        },
        body: JSON.stringify({ fileId })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const text = await response.text();
      const lines = text.trim().split('\n').filter(line => line.trim());
      const parsedContent = lines.map(line => {
        try {
          return JSON.parse(line);
        } catch (e) {
          return { error: 'Parse error', raw: line };
        }
      });

      setFileContent(parsedContent);
    } catch (error) {
      console.error("Erreur lors de la lecture du fichier:", error);
      showMessage(`${t('settings.admin.openAIBatches.viewErrorMessage')}: ${error.message}`, 'error');
      setViewingFile(null);
    } finally {
      setLoadingFileContent(false);
    }
  };

  // Fonction pour fermer la modal
  const closeFileViewer = () => {
    setViewingFile(null);
    setFileContent([]);
  };

  // Fonction pour annuler un batch
  const cancelBatch = async (batchId) => {
    // Demander confirmation
    if (!window.confirm(t('settings.admin.openAIBatches.confirmCancel'))) {
      return;
    }

    try {
      if (!functionKey) {
        throw new Error(t('settings.admin.openAIBatches.functionKeyMissing'));
      }

      setCancellingBatches(prev => ({ ...prev, [batchId]: true }));

      const baseUrl = import.meta.env.VITE_FUNCTION_URL;
      const apiUrl = `${baseUrl}/api/AskOpenAIBatchCancel`;

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          "x-functions-key": functionKey
        },
        body: JSON.stringify({ batch_id: batchId })
      });

      const result = await response.json();

      if (response.ok && result.success) {
        showMessage(t('settings.admin.openAIBatches.cancelSuccess'), 'success');
        await loadBatches();
      } else {
        throw new Error(result.error || 'API error');
      }
    } catch (error) {
      console.error("Erreur lors de l'annulation:", error);
      showMessage(`${t('settings.admin.openAIBatches.cancelError')}: ${error.message}`, 'error');
    } finally {
      setCancellingBatches(prev => {
        const newState = { ...prev };
        delete newState[batchId];
        return newState;
      });
    }
  };

  // Fonction pour vérifier si un batch peut être annulé
  const canCancelBatch = (status) => {
    return ['validating', 'in_progress', 'finalizing'].includes(status);
  };

  if (adminLoading || keyLoading) {
    return <LoadingSpinner fullScreen />;
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <PageHeader title={t('settings.admin.openAIBatches.title')} backPath="/settings" />

      <div className="p-4">
        {/* Messages */}
        {message.text && (
          <div className={`mb-4 p-3 rounded-lg ${
            message.type === 'success' ? 'bg-green-100 text-green-800' :
            message.type === 'error' ? 'bg-red-100 text-red-800' :
            message.type === 'info' ? 'bg-blue-100 text-blue-800' :
            'bg-blue-100 text-blue-800'
          }`}>
            {message.text}
          </div>
        )}

        {/* Avertissement si la clé est manquante */}
        {functionKeyMissing && (
          <div className="mb-4 p-3 rounded-lg bg-yellow-100 text-yellow-800 border border-yellow-300">
            <div className="flex items-start gap-2">
              <span className="text-lg">⚠️</span>
              <div>
                <p className="font-medium">{t('settings.admin.openAIBatches.configRequired')}</p>
                <p className="text-sm mt-1">{t('settings.admin.openAIBatches.functionKeyError')}</p>
              </div>
            </div>
          </div>
        )}

        {/* Boutons d'action */}
        <div className="flex justify-end gap-4 mb-6">
          <button
            onClick={loadBatches}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            disabled={loading}
          >
            🔄 {t('common.refresh')}
          </button>
          <button
            onClick={refreshBatches}
            disabled={refreshLoading || functionKeyMissing}
            className="bg-[#2894D8] text-white px-4 py-2 rounded-lg hover:bg-[#136E9D] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {refreshLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                {t('settings.admin.openAIBatches.refreshing')}
              </>
            ) : (
              <>
                <span>🔄</span>
                {t('settings.admin.openAIBatches.refreshFromAPI')}
              </>
            )}
          </button>
        </div>

        {/* Statistiques */}
        {batches.length > 0 && (
          <div className="mb-4 p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-between text-sm text-gray-600">
              <span>{t('settings.admin.openAIBatches.totalBatches')}: {batches.length}</span>
              <div className="flex items-center gap-4">
                <span className="text-green-600">
                  {t('settings.admin.openAIBatches.completed')}: {batches.filter(b => b.status === 'completed').length}
                </span>
                <span className="text-yellow-600">
                  {t('settings.admin.openAIBatches.inProgress')}: {batches.filter(b => b.status === 'in_progress').length}
                </span>
                <span className="text-red-600">
                  {t('settings.admin.openAIBatches.failed')}: {batches.filter(b => b.status === 'failed').length}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Modal de visualisation du fichier */}
        {viewingFile && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] flex flex-col">
              {/* Header */}
              <div className="p-6 border-b flex items-center justify-between">
                <div>
                  <h2 className="text-h2">{t('settings.admin.openAIBatches.fileContent')}</h2>
                  <p className="text-sm text-gray-500 mt-1">
                    {viewingFile.fileType === 'output' && t('settings.admin.openAIBatches.outputFileDescription')}
                    {viewingFile.fileType === 'input' && t('settings.admin.openAIBatches.inputFileDescription')}
                    {viewingFile.fileType === 'error' && t('settings.admin.openAIBatches.errorFileDescription')}
                  </p>
                </div>
                <button
                  onClick={closeFileViewer}
                  className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
                >
                  ✕
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6">
                {loadingFileContent ? (
                  <LoadingSpinner message={t('common.loading')} />
                ) : fileContent.length === 0 ? (
                  <div className="text-center text-gray-500 py-8">
                    {t('settings.admin.openAIBatches.noContent')}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {fileContent.map((item, index) => (
                      <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <span className="text-xs font-mono bg-white px-2 py-1 rounded border">
                              #{index + 1}
                            </span>
                            {item.custom_id && (
                              <span className="text-sm font-medium text-gray-700">
                                {item.custom_id}
                              </span>
                            )}
                          </div>
                          {item.response?.status_code && (
                            <span className={`text-xs px-2 py-1 rounded ${
                              item.response.status_code === 200 
                                ? 'bg-green-100 text-green-700' 
                                : 'bg-red-100 text-red-700'
                            }`}>
                              {item.response.status_code}
                            </span>
                          )}
                        </div>

                        {/* Pour les fichiers de sortie */}
                        {viewingFile.fileType === 'output' && (
                          <div className="bg-white rounded p-3 border border-gray-200">
                            <div className="text-xs text-gray-500 mb-2 font-medium">
                              {t('settings.admin.openAIBatches.response')}:
                            </div>
                            {/* Nouveau format avec output */}
                            {item.response?.body?.output?.find(o => o.type === 'message')?.content?.[0]?.text ? (
                              <>
                                <div className="text-sm text-gray-800 whitespace-pre-wrap">
                                  {item.response.body.output.find(o => o.type === 'message').content[0].text}
                                </div>
                                {item.response.body.usage && (
                                  <div className="mt-3 pt-3 border-t text-xs text-gray-500 flex gap-4">
                                    <span>Tokens: {item.response.body.usage.total_tokens}</span>
                                    <span>Input: {item.response.body.usage.input_tokens}</span>
                                    <span>Output: {item.response.body.usage.output_tokens}</span>
                                    {item.response.body.usage.output_tokens_details?.reasoning_tokens && (
                                      <span>Reasoning: {item.response.body.usage.output_tokens_details.reasoning_tokens}</span>
                                    )}
                                  </div>
                                )}
                              </>
                            ) : /* Ancien format avec choices */ 
                            item.response?.body?.choices?.[0]?.message?.content ? (
                              <>
                                <div className="text-sm text-gray-800 whitespace-pre-wrap">
                                  {item.response.body.choices[0].message.content}
                                </div>
                                {item.response.body.usage && (
                                  <div className="mt-3 pt-3 border-t text-xs text-gray-500 flex gap-4">
                                    <span>Tokens: {item.response.body.usage.total_tokens}</span>
                                    <span>Prompt: {item.response.body.usage.prompt_tokens}</span>
                                    <span>Completion: {item.response.body.usage.completion_tokens}</span>
                                  </div>
                                )}
                              </>
                            ) : (
                              <div className="text-sm text-gray-500 italic">
                                {t('settings.admin.openAIBatches.noContent')}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Pour les fichiers d'entrée */}
                        {viewingFile.fileType === 'input' && (
                          <div className="bg-white rounded p-3 border border-gray-200">
                            <div className="text-xs text-gray-500 mb-2 font-medium">
                              {t('settings.admin.openAIBatches.prompt')}:
                            </div>
                            {/* Nouveau format avec body.input (string direct) */}
                            {item.body?.input ? (
                              <div className="text-sm text-gray-800 whitespace-pre-wrap">
                                {item.body.input}
                              </div>
                            ) : /* Format avec input array */
                            item.input ? (
                              <div className="space-y-2">
                                {Array.isArray(item.input) ? (
                                  item.input.map((msg, msgIndex) => (
                                    <div key={msgIndex} className="mb-2 last:mb-0">
                                      <span className="text-xs font-medium text-gray-600">{msg.role || 'message'}:</span>
                                      <div className="text-sm text-gray-800 mt-1 whitespace-pre-wrap">
                                        {typeof msg.content === 'string' ? msg.content : JSON.stringify(msg.content, null, 2)}
                                      </div>
                                    </div>
                                  ))
                                ) : (
                                  <div className="text-sm text-gray-800 whitespace-pre-wrap">
                                    {typeof item.input === 'string' ? item.input : JSON.stringify(item.input, null, 2)}
                                  </div>
                                )}
                              </div>
                            ) : /* Ancien format avec body.messages */
                            item.body?.messages ? (
                              item.body.messages.map((msg, msgIndex) => (
                                <div key={msgIndex} className="mb-2 last:mb-0">
                                  <span className="text-xs font-medium text-gray-600">{msg.role}:</span>
                                  <div className="text-sm text-gray-800 mt-1 whitespace-pre-wrap">
                                    {msg.content}
                                  </div>
                                </div>
                              ))
                            ) : (
                              <div className="text-sm text-gray-500 italic">
                                {t('settings.admin.openAIBatches.noContent')}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Pour les fichiers d'erreur */}
                        {viewingFile.fileType === 'error' && (
                          <div className="space-y-2">
                            {/* Nouveau format avec response.body.error */}
                            {item.response?.body?.error && (
                              <div className="bg-red-50 rounded p-3 border border-red-200">
                                <div className="text-xs text-red-600 mb-2 font-medium">
                                  {t('settings.admin.openAIBatches.error')}:
                                </div>
                                <div className="space-y-2">
                                  <div className="text-sm text-red-800 font-medium">
                                    {item.response.body.error.message}
                                  </div>
                                  {item.response.body.error.type && (
                                    <div className="text-xs text-red-600">
                                      Type: {item.response.body.error.type}
                                    </div>
                                  )}
                                  {item.response.body.error.code && (
                                    <div className="text-xs text-red-600">
                                      Code: {item.response.body.error.code}
                                    </div>
                                  )}
                                  {item.response.status_code && (
                                    <div className="text-xs text-red-600">
                                      Status: {item.response.status_code}
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                            {/* Ancien format avec error direct */}
                            {item.error && !item.response?.body?.error && (
                              <div className="bg-red-50 rounded p-3 border border-red-200">
                                <div className="text-xs text-red-600 mb-2 font-medium">
                                  {t('settings.admin.openAIBatches.error')}:
                                </div>
                                <div className="text-sm text-red-800">
                                  {item.error.message || JSON.stringify(item.error)}
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Affichage brut en cas d'erreur de parsing */}
                        {item.error === 'Parse error' && (
                          <pre className="text-xs bg-gray-100 p-2 rounded overflow-x-auto">
                            {item.raw}
                          </pre>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="p-4 border-t bg-gray-50 flex justify-between items-center">
                <div className="text-sm text-gray-600">
                  {fileContent.length} {t('settings.admin.openAIBatches.items')}
                </div>
                <button
                  onClick={closeFileViewer}
                  className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors"
                >
                  {t('common.close')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Tableau des batchs */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="p-4 border-b">
            <h2 className="text-h2">{t('settings.admin.openAIBatches.batchList')}</h2>
          </div>
          {loading ? (
            <LoadingSpinner message={t('common.loading')} />
          ) : batches.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              {t('settings.admin.openAIBatches.noBatches')}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('settings.admin.openAIBatches.status')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('settings.admin.openAIBatches.imported')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('settings.admin.openAIBatches.endpoint')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('settings.admin.openAIBatches.created')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('settings.admin.openAIBatches.requests')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('settings.admin.openAIBatches.error')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('settings.admin.openAIBatches.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {batches.map((batch) => (
                    <tr key={batch.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                        {batch.id.substring(0, 20)}...
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(batch.status)}`}>
                          {batch.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          batch.imported === true ? 'bg-green-100 text-green-800' : 
                          batch.imported === false ? 'bg-gray-100 text-gray-800' : 
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {batch.imported === true ? 'TRUE' : batch.imported === false ? 'FALSE' : '-'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {batch.endpoint}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(batch.created_at_timestamp || batch.created_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex flex-col gap-1">
                          <span>Total: {batch.request_counts?.total || 0}</span>
                          <span className="text-green-600">✓ {batch.request_counts?.completed || 0}</span>
                          <span className="text-red-600">✗ {batch.request_counts?.failed || 0}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {batch.errors ? (
                          <div className="max-w-xs text-xs text-red-600">
                            {typeof batch.errors === 'string' 
                              ? batch.errors 
                              : batch.errors.data?.[0]?.message || batch.errors.message || JSON.stringify(batch.errors)
                            }
                          </div>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <div className="flex flex-col gap-2">
                          {batch.input_file_id && (
                            <div className="flex gap-2">
                              <button
                                onClick={() => viewFileContent(batch.input_file_id, 'input', batch.id)}
                                disabled={functionKeyMissing}
                                className="flex-1 px-3 py-1 text-xs bg-blue-50 text-blue-700 rounded hover:bg-blue-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 justify-center"
                              >
                                👁️ {t('settings.admin.openAIBatches.viewInput')}
                              </button>
                              <button
                                onClick={() => downloadFile(batch.input_file_id, `input-${batch.id}.jsonl`, batch.id)}
                                disabled={downloadingFiles[`${batch.id}-${batch.input_file_id}`] || functionKeyMissing}
                                className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 justify-center"
                              >
                                {downloadingFiles[`${batch.id}-${batch.input_file_id}`] ? (
                                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-700"></div>
                                ) : (
                                  '📥'
                                )}
                              </button>
                            </div>
                          )}
                          {batch.output_file_id && (
                            <div className="flex gap-2">
                              <button
                                onClick={() => viewFileContent(batch.output_file_id, 'output', batch.id)}
                                disabled={functionKeyMissing}
                                className="flex-1 px-3 py-1 text-xs bg-green-50 text-green-700 rounded hover:bg-green-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 justify-center"
                              >
                                👁️ {t('settings.admin.openAIBatches.viewOutput')}
                              </button>
                              <button
                                onClick={() => downloadFile(batch.output_file_id, `output-${batch.id}.jsonl`, batch.id)}
                                disabled={downloadingFiles[`${batch.id}-${batch.output_file_id}`] || functionKeyMissing}
                                className="px-3 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 justify-center"
                              >
                                {downloadingFiles[`${batch.id}-${batch.output_file_id}`] ? (
                                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-green-700"></div>
                                ) : (
                                  '📥'
                                )}
                              </button>
                            </div>
                          )}
                          {batch.error_file_id && (
                            <div className="flex gap-2">
                              <button
                                onClick={() => viewFileContent(batch.error_file_id, 'error', batch.id)}
                                disabled={functionKeyMissing}
                                className="flex-1 px-3 py-1 text-xs bg-red-50 text-red-700 rounded hover:bg-red-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 justify-center"
                              >
                                👁️ {t('settings.admin.openAIBatches.viewError')}
                              </button>
                              <button
                                onClick={() => downloadFile(batch.error_file_id, `error-${batch.id}.jsonl`, batch.id)}
                                disabled={downloadingFiles[`${batch.id}-${batch.error_file_id}`] || functionKeyMissing}
                                className="px-3 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 justify-center"
                              >
                                {downloadingFiles[`${batch.id}-${batch.error_file_id}`] ? (
                                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-red-700"></div>
                                ) : (
                                  '📥'
                                )}
                              </button>
                            </div>
                          )}
                          {canCancelBatch(batch.status) && (
                            <button
                              onClick={() => cancelBatch(batch.id)}
                              disabled={cancellingBatches[batch.id] || functionKeyMissing}
                              className="px-3 py-1 text-xs bg-orange-100 text-orange-700 rounded hover:bg-orange-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 justify-center"
                            >
                              {cancellingBatches[batch.id] ? (
                                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-orange-700"></div>
                              ) : (
                                '🚫'
                              )}
                              {t('settings.admin.openAIBatches.cancelBatch')}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OpenAIBatchesPage;
